import {
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	Icon,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	Tag,
	type TagProps,
	Text
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import type { SeasonTheme } from "@/app/utilities/types";

import * as styles from "../shared.module.scss";

export interface FeaturedRecipeProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> {
	theme?: SeasonTheme;
	mainRecipe: {
		image: ImageProps<ImageComponentProps>;
		tag: TagProps;
		title: string;
		prepTime: string;
		description: string;
		cta: LinkProps<LinkComponentProps>;
	};
	relatedProducts: {
		title: string;
		productCards: {
			image: ImageProps<ImageComponentProps>;
			title: string;
			price?: PriceProps;
			rating?: RatingProps;
			cta: LinkProps<LinkComponentProps>;
		}[];
	};
}

/**
 * Featured Recipe component
 */
export function FeaturedRecipe<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	theme = "standard",
	...props
}: FeaturedRecipeProps<ImageComponentProps, LinkComponentProps>): JSX.Element {
	return (
		<div
			{...classNameHelper(
				styles["featured"],
				theme && styles[`featured--${theme}`]
			)}
		>
			<Icon
				name="featured-background"
				className={styles["featured__background"]}
			/>
			<Container>
				<article className={styles["featured__main"]}>
					<Image
						{...props.mainRecipe?.image}
						className={styles["featured__main-image"]}
						alt={
							props.mainRecipe?.image.alt ||
							"Featured Product Recipe Image"
						}
					/>
					<div>
						<Tag {...props.mainRecipe?.tag} />
						<Heading
							tag="h2"
							styledAs="h1"
							className={styles["featured__main-title"]}
						>
							{props.mainRecipe?.title}
						</Heading>
						<div className={styles["featured__main-details"]}>
							<Text styledAs="xsr" className="mb-0">
								<Icon name="Clock" className="mr-1" />
								{props.mainRecipe?.prepTime}
							</Text>
						</div>
						<Text
							styledAs="xsr"
							className={styles["featured__main-description"]}
						>
							{props.mainRecipe?.description}
						</Text>
						<Link
							{...props.mainRecipe?.cta}
							kind="primary"
							className={styles["featured__main-cta"]}
						/>
					</div>
				</article>
				<div className={styles["featured__related-container"]}>
					{props.relatedProducts?.title && (
						<Heading tag="h2" styledAs="h2">
							{props.relatedProducts?.title}
						</Heading>
					)}
					<div className={styles["featured__related"]}>
						{props.relatedProducts?.productCards.map(
							(card, index) => (
								<article
									key={index}
									className={styles["featured__related-card"]}
								>
									<Image
										{...card?.image}
										{...classNameHelper(
											styles["featured__related-image"],
											styles[
												"featured__related-image--desktop"
											]
										)}
										alt={
											card?.image.alt ||
											"Featured Product Recipe Related Image"
										}
									/>
									<div
										className={
											styles["featured__related-content"]
										}
									>
										<Heading
											tag="h3"
											styledAs="h3"
											className={
												styles[
													"featured__related-title"
												]
											}
										>
											{card?.title}
										</Heading>
										<div
											className={
												styles[
													"featured__related-details"
												]
											}
										>
											{card?.rating && (
												<Rating
													{...card?.rating}
													className="mb-1"
												/>
											)}
											{card?.price && (
												<Price {...card?.price} />
											)}
										</div>
										<Image
											{...card?.image}
											{...classNameHelper(
												styles[
													"featured__related-image"
												],
												styles[
													"featured__related-image--mobile"
												]
											)}
											alt={
												card?.image.alt ||
												"Featured Product Recipe Related Image Mobile"
											}
										/>
										<Link
											className={
												styles["featured__related-cta"]
											}
											{...card?.cta}
											kind="primary"
											size="md"
										/>
									</div>
								</article>
							)
						)}
					</div>
				</div>
			</Container>
		</div>
	);
}
